import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  makeStyles,
} from '@material-ui/core'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import SearchBar from 'components/Forms/SearchBar'
import useProductManagement from 'features/productManagement/useProductManagement'
import {navigate} from 'AppHistory'
import RouteConstants from 'constants/RouteConstant'
import ProductsManagementStyle from 'pages/ProductsPages/ProductsList/Style'
import {ProductManagementSearchPagingRequest} from 'api/productApi'
import {OFFER_TYPE} from '../constant'

export enum SearchKeys {
  all = 'all',
  hospital = 'Hospital',
  healthcare = 'Healthcare',
  subscription = 'Subscription',
  prepaid = 'Prepaid',
  prepaidOne = 'PrepaidOne',
  postpaid = 'Postpaid',
  manualpaid = 'ManualPaid',
  domestic = 'Domestic',
  overseas = 'Overseas',
  productName = 'ProductName',
  orgId = 'OrgId',
  APPOINT = 'APPOINT',
}

const SELECT_ORGANIZATION: SelectItem<any>[] = [
  {label: 'ITargetOrganization', value: SearchKeys.all},
  {label: 'IClinicAndHospital', value: SearchKeys.hospital},
  {label: 'IExaminationCenter', value: SearchKeys.healthcare},
]

const SELECT_TYPE: SelectItem<any>[] = [
  {label: 'IOperatingProductType', value: SearchKeys.all},
  {label: 'ISubscription', value: SearchKeys.prepaidOne},
  {label: 'IPrepaid', value: SearchKeys.prepaid},
  {label: 'IManual', value: SearchKeys.manualpaid},
]

const SELECT_REGION: SelectItem<any>[] = [
  {label: 'ITargetNation', value: SearchKeys.all},
  {label: 'IDomestic', value: SearchKeys.domestic},
  {label: 'IOverseas', value: SearchKeys.overseas},
]

const SELECT_PRODUCT_NAME: SelectItem<any>[] = [
  {label: 'IProductNameSelect', value: SearchKeys.productName},
]
const TableToolBar = () => {
  const {t, i18n} = useTranslation()
  const useStyles = makeStyles(ProductsManagementStyle)
  const classes = useStyles()
  const {onSearchPaging} = useProductManagement()

  const [organization, setOrganization] = useState<SearchKeys>(SearchKeys.all)
  const [paymentMethod, setPaymentMethod] = useState<SearchKeys>(SearchKeys.all)
  const [targets, setTargets] = useState<string>('')
  const [isAdminDesignAgency, setIsAdminDesignAgency] = useState<boolean>(false)
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [region, setRegion] = useState<SearchKeys>(SearchKeys.all)
  const [product, setProduct] = useState<SearchKeys>(SearchKeys.productName)
  const [productDataInquiry, setProductDataInquiry] = useState<boolean>(true)

  const payload = useMemo(
    () => ({
      page: 0,
      size: 10,
      ...(productDataInquiry !== undefined
        ? {archived: productDataInquiry}
        : {}),
      ...(targets && targets.length > 0 ? {targets} : {}),
      ...(paymentMethod && paymentMethod !== SearchKeys.all
        ? {paymentMethod}
        : {}),
      ...(searchKeyword && searchKeyword.length > 0
        ? {goodsName: searchKeyword}
        : {}),
    }),
    [paymentMethod, searchKeyword, targets, productDataInquiry],
  )

  const listProductDataInquiry = [
    {
      label: 'ON',
      value: 'on',
    },
    {
      label: 'OFF',
      value: 'off',
    },
    {
      label: 'TOTAL',
      value: 'all',
    },
  ]

  const updateTarget = (category: string, value: SearchKeys) => {
    const splitTargets = targets
      .split(';')
      .filter((target) => target.trim() !== '')
    let entries: string[]
    const categoryPrefix = `${category},`
    if (value === SearchKeys.all) {
      entries = splitTargets.filter(
        (entry) => !entry.startsWith(categoryPrefix),
      )
    } else {
      entries = splitTargets.filter(
        (entry) => !entry.startsWith(categoryPrefix),
      )
      entries.push(`${category},${value}`)
    }
    const newTargets = entries.join(';')

    setTargets(newTargets)
    if (newTargets.length > 0) {
      onSearchPaging({
        ...payload,
        targets: newTargets,
      })
    } else {
      delete payload.targets
      onSearchPaging(payload)
    }
  }

  const handleSelectOrganization = (
    event: React.ChangeEvent<{value: unknown}>,
  ) => {
    const value = event.target.value as SearchKeys
    setOrganization(value)
    updateTarget('OrgType', value)
  }

  const handleSelectPaymentMethod = (
    event: React.ChangeEvent<{value: unknown}>,
  ) => {
    const value = event.target.value as SearchKeys
    setPaymentMethod(value)

    const updatedPayload: ProductManagementSearchPagingRequest = {...payload}

    if (value === SearchKeys.all) {
      delete updatedPayload.paymentMethod
    } else {
      updatedPayload.paymentMethod = value

      if (value === SearchKeys.prepaid) {
        updatedPayload.offerType = OFFER_TYPE.MONTHLY
      } else if (value === SearchKeys.prepaidOne) {
        updatedPayload.paymentMethod = SearchKeys.prepaid
        updatedPayload.offerType = OFFER_TYPE.ONCE
      } else {
        delete updatedPayload.offerType
      }
    }

    onSearchPaging(updatedPayload)
  }

  const handleSelectRegion = (event: React.ChangeEvent<{value: unknown}>) => {
    const value = event.target.value as SearchKeys
    setRegion(value)
    updateTarget('Region', value)
  }

  const handleSelectProduct = (event: React.ChangeEvent<{value: unknown}>) => {
    const value = event.target.value as SearchKeys
    setProduct(value)
  }

  const handleValue = (value: string) => {
    setSearchKeyword(value)
  }
  const handleSearch = () => {
    onSearchPaging(payload)
  }
  const handleAddClick = () => {
    navigate(RouteConstants.PRODUCTS_MGS_CREATE.path)
  }

  const handleChangeCheckIsAdminDesignAgency = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsAdminDesignAgency(event.target.checked)
    if (event.target.checked) {
      updateTarget('OrgId', SearchKeys.APPOINT)
    } else {
      // Remove OrgId from targets
      updateTarget('OrgId', SearchKeys.all)
    }
  }

  const onChangeProductDataInquiry = (value: string) => {
    let result: boolean | undefined
    if (value === 'on') {
      result = true
    } else if (value === 'off') {
      result = false
    } else {
      result = undefined
    }

    setProductDataInquiry(result)

    const updatedPayload: ProductManagementSearchPagingRequest = {
      ...payload,
      ...(value === 'all' ? {} : {archived: value === 'on'}),
    }

    if (value === 'all') {
      delete updatedPayload.archived
    }

    onSearchPaging(updatedPayload)
  }

  useEffect(() => {
    onSearchPaging(payload)
  }, [])
  return (
    <div className={classes.flexCol}>
      <div className={classes.toolBar}>
        <div className={classes.searchBar}>
          <FormControl variant='outlined'>
            <Select
              value={organization}
              onChange={handleSelectOrganization}
              style={{height: 40, width: i18n.language === 'ko' ? 105 : 185}}
              input={<OutlinedInput name='age' id='outlined-age-simple' />}
            >
              {SELECT_ORGANIZATION.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant='outlined'>
            <Select
              value={region}
              onChange={handleSelectRegion}
              style={{height: 40, width: i18n.language === 'ko' ? 105 : 190}}
              input={<OutlinedInput name='age' id='outlined-age-simple' />}
            >
              {SELECT_REGION.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant='outlined'>
            <Select
              value={paymentMethod}
              onChange={handleSelectPaymentMethod}
              style={{height: 40, width: i18n.language === 'ko' ? 190 : 245}}
              input={<OutlinedInput name='age' id='outlined-age-simple' />}
            >
              {SELECT_TYPE.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant='outlined'>
            <Select
              value={product}
              onChange={handleSelectProduct}
              className={classes.searchSelectName}
              input={<OutlinedInput name='age' id='outlined-age-simple' />}
            >
              {SELECT_PRODUCT_NAME.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <SearchBar
            onChangeValue={handleValue}
            onSearchEvent={handleSearch}
            disabledValue={false}
          />
        </div>
        <ActionButton
          onClick={handleAddClick}
          className={classes.flexRight}
          color='primary'
        >
          {t('IAdd')}
        </ActionButton>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 40,
          alignItems: 'center',
        }}
      >
        <FormControlLabel
          labelPlacement='start'
          control={
            <Checkbox
              checked={isAdminDesignAgency}
              onChange={handleChangeCheckIsAdminDesignAgency}
              name={t('IAdminDesignAgency')}
            />
          }
          label={
            <h5
              style={{
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              {t('IAdminDesignAgency')}
            </h5>
          }
          className={classes.checkboxLabel}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <h5
            style={{
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            {t('IProductDataInquiry')}
          </h5>
          <RadioGroup
            defaultValue='on'
            name='radio-buttons-group'
            row
            onChange={(e) => onChangeProductDataInquiry(e.target.value)}
          >
            {listProductDataInquiry.map((item) => (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={<Radio color='primary' />}
                label={item.label}
              />
            ))}
          </RadioGroup>
        </div>
      </div>
    </div>
  )
}

export default TableToolBar
